"use client";

import MediaImage from "@/components/ui/MediaImage";

import type { CSSProperties } from "react";
import Link from "next/link";
import PropertyLabel from "./ui/PropertyLabel";
import type { CaseData } from "@/cms/models";
import type { EditorialCardStyle } from "@/cms/appearance";
import { getCaseUrl } from "@/utils/case-url";
import { getLabel } from "@/utils/case-label";
import { getCompactPropertyFacts } from "@/utils/compact-property-facts";
import { surfaceStyle } from "@/utils/visual-style";
import { editorialLabelSurface, resolveSurface } from "@/utils/visual-defaults";
import CompactPropertyFactValue from "./ui/CompactPropertyFactValue";

export default function PropertyEditorialCard({
  property,
  layout = "grid",
  options = {},
  cardPhoto = false,
}: {
  property: CaseData;
  layout?: "grid" | "horizontal";
  options?: EditorialCardStyle;
  /** In a look-system card: its photo zooms by the section's motion level (src/styles/motion.css), not on its own. */
  cardPhoto?: boolean;
}) {
  const facts = getCompactPropertyFacts(property).facts;
  const type = facts.find((fact) => fact.key === "estateType");
  const companions = facts.filter((fact) => fact.key !== "estateType");
  const ordered = [companions[0], type, companions[1]];
  const image = property.images?.find((photo) => photo.lgUrl || photo.mdUrl);
  const imageUrl = image?.lgUrl || image?.mdUrl || property.imageUrl;
  const label = getLabel(property);
  const title =
    property.marketingTexts?.headline ||
    (
      property.shortAddress || property.advertisementAddressOrDisplayName
    ).replace(/[,\s]+$/, "");
  const address = property.advertisementAddressOrDisplayName.includes(
    property.zipAndCity,
  )
    ? property.advertisementAddressOrDisplayName
    : `${property.advertisementAddressOrDisplayName}, ${property.zipAndCity}`;

  return (
    <Link
      href={getCaseUrl(property)}
      className="re-editorial-card"
      data-component-type="PropertyEditorialCard"
      data-layout={layout}
      data-action={
        layout === "horizontal"
          ? "always"
          : (options.actionVisibility ?? "hover")
      }
      style={
        {
          "--re-card-ratio": options.imageRatio ?? "16/9",
          "--re-card-radius": options.imageRadius ?? "20px",
          "--re-card-horizontal-height": options.horizontalMinHeight ?? "400px",
          "--re-card-image-width": options.imageWidth ?? "55%",
          "--re-card-zoom": Math.max(1, Math.min(1.2, options.zoom ?? 1.03)),
          "--re-card-zoom-duration": `${options.zoomDuration ?? 300}ms`,
        } as CSSProperties
      }
    >
      <div className="re-card-media">
        {imageUrl ? (
          <MediaImage
            src={imageUrl}
            alt={property.advertisementAddressOrDisplayName}
            className="re-card-photo"
            data-re-card-photo={cardPhoto ? "" : undefined}
            loading="lazy"
            width={800}
            height={450}
          />
        ) : (
          <span>Ingen billede tilgængeligt</span>
        )}
        {label && (
          <PropertyLabel
            legacy={
              <span
                className="re-card-label re-surface"
                style={surfaceStyle(
                  resolveSurface(editorialLabelSurface, options.labelSurface),
                )}
              >
                {label}
              </span>
            }
          >
            {label}
          </PropertyLabel>
        )}
      </div>
      <div className="re-card-content">
        <h3 className="re-card-title">{title}</h3>
        <p className="re-card-address">
          {property.marketingTexts?.headline ? address : property.zipAndCity}
        </p>
        <p className="re-card-price">{property.priceInfo || "-"}</p>
        <dl className="re-card-facts">
          {ordered.map((fact, index) =>
            fact ? (
              <div key={fact.key}>
                <dt>{fact.label}</dt>
                <dd>
                  <CompactPropertyFactValue fact={fact} />
                </dd>
              </div>
            ) : (
              <div key={`empty-${index}`} aria-hidden="true" />
            ),
          )}
        </dl>
        <div className="re-card-action" aria-hidden="true">
          <span className="re-action re-action-pill">
            {options.actionLabel ?? "Se bolig"}
          </span>
        </div>
      </div>
    </Link>
  );
}
